import React, { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { getCampaignRecipients, createCampaign, updateCampaign } from "./campaignService"
import { FiChevronLeft, FiChevronRight, FiArrowLeft } from "react-icons/fi"

export default function CampaignBuilder() {
  const location = useLocation()
  const navigate = useNavigate()
  const [testEmail, setTestEmail] = useState("")

  // Campaign data from previous step
  const campaignData = location.state?.campaignData || JSON.parse(sessionStorage.getItem("campaignData") || "{}")

  const handleBackToForm = () => {
    navigate("/admin/marketing/campaign-create", {
      state: { campaignData, fromBuilderBack: true },
    })
  }

  // Recipients state
  const [recipients, setRecipients] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalRecipients, setTotalRecipients] = useState(0)
  const [loadingRecipients, setLoadingRecipients] = useState(false)
  const [recipientsError, setRecipientsError] = useState("")
  const itemsPerPage = 10

  // Campaign submission state
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [submitSuccess, setSubmitSuccess] = useState(false)

  // Test email state
  const [sendingTest, setSendingTest] = useState(false)
  const [testEmailMessage, setTestEmailMessage] = useState("")

  // Fetch recipients when campaign data is available
  useEffect(() => {
    if (campaignData?.campaignName) {
      fetchRecipients(1)
    }
  }, [campaignData])

  const fetchRecipients = async (page) => {
    setLoadingRecipients(true)
    setRecipientsError("")
    try {
      // Note: getCampaignRecipients requires a valid campaign ID
      // Since campaign hasn't been created yet, we skip fetching actual recipients
      // and use the estimated count instead
      // Recipients will be fetched after campaign creation
      setTotalRecipients(campaignData.estimatedCount || 0)
      setRecipients([])
    } catch (err) {
      console.error("Failed to fetch recipients:", err)
      // Fallback: use estimated count
      setTotalRecipients(campaignData.estimatedCount || 0)
      setRecipients([])
    } finally {
      setLoadingRecipients(false)
    }
  }

  const handlePageChange = (newPage) => {
    const maxPage = Math.ceil(totalRecipients / itemsPerPage)
    if (newPage >= 1 && newPage <= maxPage) {
      setCurrentPage(newPage)
      fetchRecipients(newPage)
    }
  }

  const handleSendCampaign = async () => {
    setSubmitting(true)
    setSubmitError("")
    setSubmitSuccess(false)

    try {
      const payload = {
        contactGroup: Array.isArray(campaignData.selectedContactGroups)
          ? campaignData.selectedContactGroups.join(",")
          : campaignData.selectedContactGroups,
        campaignSubject: campaignData.campaignSubject,
        agencyName: campaignData.agencyName,
        isEmail: campaignData.isEmail,
        isSms: campaignData.isSms,
        emailBody: campaignData.emailBody,
        smsBody: campaignData.smsBody,
        campaignName: campaignData.campaignName,
        scheduledTime: campaignData.scheduledTime,
        attachments: campaignData.attachments,
      }

      // Check if we're in edit mode (updating a draft)
      const isEditMode = campaignData.isEditMode && campaignData.campaignId

      let response
      if (isEditMode) {
        // Update existing campaign - remove draft status so it becomes pending
        const updatePayload = {
          ...payload,
          status: "pending"
        }
        response = await updateCampaign(campaignData.campaignId, updatePayload)
      } else {
        // Create new campaign
        response = await createCampaign(payload)
      }

      if (response?.code === 200 || response?.body?.code === 200) {
        setSubmitSuccess(true)
        sessionStorage.removeItem("campaignData")
        setTimeout(() => {
          // Navigate with refetch flag to trigger data reload
          navigate("/admin/marketing/campaigns", { state: { refetch: true } })
        }, 2000)
      } else {
        setSubmitError(response?.message || response?.body?.message || "Failed to send campaign")
      }
    } catch (err) {
      console.error("Campaign submission error:", err)
      setSubmitError(err.message || "Failed to send campaign")
    } finally {
      setSubmitting(false)
    }
  }

  const handleDraftCampaign = async () => {
    try {
      const payload = {
        contactGroup: Array.isArray(campaignData.selectedContactGroups)
          ? campaignData.selectedContactGroups.join(",")
          : campaignData.selectedContactGroups,
        campaignSubject: campaignData.campaignSubject,
        agencyName: campaignData.agencyName,
        isEmail: campaignData.isEmail,
        isSms: campaignData.isSms,
        status: "draft",
        emailBody: campaignData.emailBody,
        smsBody: campaignData.smsBody,
        campaignName: campaignData.campaignName,
        scheduledTime: campaignData.scheduledTime,
        attachments: campaignData.attachments,
      }

      console.log('**** PAYLOAD :::: ', payload);

      // Check if we're in edit mode (updating a draft)
      const isEditMode = campaignData.isEditMode && campaignData.campaignId

      let response
      if (isEditMode) {
        // Update existing draft campaign
        response = await updateCampaign(campaignData.campaignId, payload)
      } else {
        // Create new draft campaign
        response = await createCampaign(payload)
      }

      if (response?.code === 200 || response?.body?.code === 200) {
        setSubmitSuccess(true)
        sessionStorage.removeItem("campaignData")
        setTimeout(() => {
          // Navigate with refetch flag to trigger data reload
          navigate("/admin/marketing/campaigns", { state: { refetch: true } })
        }, 2000)
      } else {
        setSubmitError(response?.message || response?.body?.message || "Failed to send campaign")
      }
    } catch (err) {
      console.error("Campaign submission error:", err)
      setSubmitError(err.message || "Failed to send campaign")
    } finally {
      setSubmitting(false)
    }

  }


  const handleSendTestEmail = async () => {
    if (!testEmail.trim()) {
      setTestEmailMessage("Please enter a valid email address")
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(testEmail)) {
      setTestEmailMessage("Please enter a valid email address")
      return
    }

    setSendingTest(true)
    setTestEmailMessage("")

    try {
      // TODO: Implement actual test email API endpoint
      // For now, just log to console and clear input
      console.log("Test email functionality - pending backend API implementation")
      console.log("Email:", testEmail)

      setTestEmail("")
      // No success message shown since this is not yet implemented
    } catch (err) {
      console.error("Failed to send test email:", err)
      setTestEmailMessage("Failed to send test email. Please try again.")
    } finally {
      setSendingTest(false)
    }
  }

  const campaignType = []
  if (campaignData.isEmail) campaignType.push("Email")
  if (campaignData.isSms) campaignType.push("SMS")

  const totalPages = Math.ceil(totalRecipients / itemsPerPage)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={handleBackToForm}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Back to Campaign Form"
        >
          <FiArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold mb-2">Campaign Builder</h1>
          <p className="text-gray-600 text-sm md:text-base">Create and send email campaigns</p>
        </div>
      </div>

      {submitSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 text-sm">
          Campaign sent successfully! Redirecting...
        </div>
      )}

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          {submitError}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm w-full max-w-3xl">
        <h2 className="text-base font-medium text-gray-800 mb-4">Review & Send</h2>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Campaign Name:</span>
            <span className="text-gray-900 font-medium break-all">{campaignData.campaignName || "N/A"}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Campaign Type:</span>
            <span className="text-gray-900 font-medium">{campaignType.join(" & ") || "N/A"}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Contact Groups:</span>
            <span className="text-gray-900 font-medium">
              {Array.isArray(campaignData.selectedContactGroups) && campaignData.selectedContactGroups.length > 0
                ? campaignData.selectedContactGroups.join(", ")
                : "N/A"}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Recipients:</span>
            <span className="text-gray-900 font-medium">{totalRecipients || campaignData.estimatedCount || 0} recipients</span>
          </div>
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Delivery:</span>
            <span className="text-gray-900 font-medium">{campaignData.sendNow ? "Immediate" : "Scheduled"}</span>
          </div>
        </div>
      </div>

      {/* Recipients Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 md:p-6 shadow-sm w-full max-w-3xl">
        <h3 className="text-base font-medium text-gray-800 mb-4">Recipients Summary</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Total Recipients:</span>
            <span className="text-gray-900 font-medium">{totalRecipients || campaignData.estimatedCount || 0}</span>
          </div>
          <div className="flex items-center justify-between gap-4 border rounded-md px-4 py-3">
            <span className="text-gray-700">Contact Groups:</span>
            <span className="text-gray-900 font-medium text-sm">
              {Array.isArray(campaignData.selectedContactGroups)
                ? campaignData.selectedContactGroups.join(", ")
                : "N/A"}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-4">
            Recipients will be fetched and displayed after campaign creation. The estimated count above is based on the selected contact groups.
          </p>
        </div>
      </div>

      <div className="w-full max-w-3xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={handleSendCampaign}
            disabled={submitting}
            className="bg-black text-white py-2.5 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Send Campaign"}
          </button>
          <button
            onClick={handleDraftCampaign}
            // onClick={() => navigate("/admin/marketing/campaign-create")}
            className="bg-gray-500 text-white py-2.5 rounded-md hover:bg-gray-600 transition-colors"
          >
            Save as Draft
          </button>
        </div>
      </div>
    </div>
  )
}